import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { makeStyles } from "@material-ui/core";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
} from "../pl-by-program/plByProgramTheme";
import { formatActualsThroughDate } from "../pl-by-program/plTimeFilterUtils";
import {
  buildWaterfallEacApiFilters,
  buildWaterfallEacDownloadFilters,
  getApiFilterKey,
  aggregateThreeYearRows,
} from "./waterfallEacFilterUtils";
import { hasAnyTableFilters } from "../tableTotalUtils";
import { useReportFonts } from "../../reportFonts";
import {
  getThreeYearViewAPI,
  downloadThreeYearViewAPI,
} from "../../../../api/reports-api";
import Loading from "../../../../common-components/Loading";
import { calcFilterPos, calcOpDropdownPos } from "./filterPopoverUtils";
import {
  NumericFilterContent,
  OpDropdownContent,
} from "./tableColumnFilterShared";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
  fmtPctDisplay,
  cellWhiteSpaceStyle,
  dynamicColSize,
} from "./waterfallTableUtils";

import {
  TH_BG,
  TH_BG_BAND,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  ACCENT_COLOR,
  Z_HEAD,
  Z_STICKY,
} from "../../reportTableConfig";

/* ─── Design tokens ─────────────────────────────────────────────── */
const NEG = "#D64550";
const TOTAL_FG = CELL_FG_STRONG;
const ACCENT = ACCENT_COLOR;
const ACCENT2 = "#EEF2FF";

/* ─── Operator config ───────────────────────────────────────────── */
const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt", label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt", label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq", label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

function applyNumFilter(v, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const n = Number(v ?? 0),
    fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":
      return n > fv;
    case "gte":
      return n >= fv;
    case "lt":
      return n < fv;
    case "lte":
      return n <= fv;
    case "eq":
      return Math.abs(n - fv) < 0.001;
    case "neq":
      return Math.abs(n - fv) >= 0.001;
    default:
      return true;
  }
}

/* ─── Fixed columns ─────────────────────────────────────────────── */
const FIXED_DEFS = [
  { key: "projectNum", label: "Project #", w: 102, align: "center" },
  { key: "contractName", label: "Contract Name", w: 200, align: "left" },
  { key: "customerName", label: "Customer Name", w: 172, align: "left" },
  { key: "bu", label: "BU", w: 110, align: "left" },
  { key: "pm", label: "Program Manager", w: 172, align: "left" },
  { key: "popStart", label: "POP Start Date", w: 130, align: "center" },
  { key: "popEnd", label: "POP End Date", w: 120, align: "center" },
  { key: "contractType", label: "Contract Type", w: 128, align: "center" },
  {
    key: "tcvCeiling",
    label: "TCV Ceiling",
    w: 130,
    align: "right",
    isNum: true,
  },
  {
    key: "tcvFunded",
    label: "EAC Account TCV Funded",
    w: 220,
    align: "right",
    isNum: true,
  },
];
let _fixLeft = 0;
FIXED_DEFS.forEach((c) => {
  c.left = _fixLeft;
  _fixLeft += c.w;
});

/* ─── Year metrics (9 measures per year group) ──────────────────── */
const YEAR_METRICS = [
  { key: "contractRevenue", label: "Revenue", w: 128, isPct: false },
  { key: "directLabor", label: "Direct Labor", w: 130, isPct: false },
  { key: "subcontractor", label: "Subcontractor", w: 136, isPct: false },
  { key: "odcs", label: "ODCs", w: 100, isPct: false },
  { key: "totalDirectCost", label: "Total Direct Cost", w: 164, isPct: false },
  { key: "grossMargin", label: "Gross Margin", w: 136, isPct: false },
  { key: "grossMarginPct", label: "GM%", w: 78, isPct: true },
  { key: "netProfit", label: "Net Profit / Fee", w: 162, isPct: false },
  { key: "netMarginPct", label: "NM%", w: 78, isPct: true },
];

const YEAR_GROUPS = [
  { key: "year0", offset: 0 },
  { key: "year1", offset: 1 },
  { key: "year2", offset: 2 },
];

/* ─── Layout constants ──────────────────────────────────────────── */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const GH = 32;
const CH = 34;

/* ─── Default hidden columns ────────────────────────────────────── */
const DEFAULT_HIDDEN = new Set([
  "bu",
  "pm",
  "popStart",
  "popEnd",
  "contractType",
]);

/* ─── Data helpers ──────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function transformMeasures(raw) {
  if (!raw) return {};
  const out = {};
  for (const m of YEAR_METRICS) {
    const v = raw[m.key] ?? null;
    out[m.key] = m.isPct && v != null ? v * 100 : v;
  }
  return out;
}

function transformContract(contract) {
  const years = contract.years || [];
  return {
    projectNum: contract.id,
    contractName: contract.contractName,
    customerName: contract.customerName,
    bu: contract.bu,
    pm: contract.programManager || "",
    popStart: fmtDate(contract.popStartDate),
    popEnd: fmtDate(contract.popEndDate),
    contractType: contract.contractType || "",
    tcvCeiling: contract.ceiling,
    tcvFunded: contract.funded,
    year0: transformMeasures(years[0]?.measures),
    year1: transformMeasures(years[1]?.measures),
    year2: transformMeasures(years[2]?.measures),
  };
}

function transformGrandTotals(grandTotals) {
  const gt = grandTotals || [];
  return {
    projectNum: "",
    contractName: "Total",
    customerName: "",
    bu: "",
    pm: "",
    popStart: "",
    popEnd: "",
    contractType: "",
    tcvCeiling: null,
    tcvFunded: null,
    isTotal: true,
    year0: transformMeasures(gt[0]?.measures),
    year1: transformMeasures(gt[1]?.measures),
    year2: transformMeasures(gt[2]?.measures),
  };
}

/* ─── Formatters ────────────────────────────────────────────────── */
const fmtMoney2 = (v) => {
  if (v == null || v === "") return "";
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
const fmtCell = (v, isPct) => (isPct ? fmtPctDisplay(v) : fmtMoney2(v));

/* ─── Styles ────────────────────────────────────────────────────── */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "18px 24px 14px",
    borderBottom: "1px solid #E2E8F0",
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    flexShrink: 0,
    background: "#FFFFFF",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.02em",
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: "#6B7280",
    fontFamily: PL_FONT,
  },
  tableScrollPane: { overflowX: "auto" },
  tableBody: {
    overflow: "hidden",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  table: {
    borderCollapse: "collapse",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontFamily: PL_FONT,
    fontSize: 11,
  },

  sectionHeadRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionHeadRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  /* column picker */
  colPickerWrap: { position: "relative", flexShrink: 0 },
  colPickerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 34,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid #E2E8F0",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: PL_FONT,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#CBD5E1" },
  },
  colHiddenBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4338CA",
    background: "rgba(99,102,241,0.12)",
    borderRadius: 4,
    padding: "1px 6px",
    marginLeft: 2,
  },
  colPickerDropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    padding: "6px 0 8px",
    minWidth: 220,
    maxHeight: 420,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  colPickerHeader: {
    fontSize: 10,
    fontWeight: 700,
    color: "#9CA3AF",
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    padding: "6px 16px 8px",
    fontFamily: PL_FONT,
    borderBottom: "1px solid #F3F4F6",
    marginBottom: 4,
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 16px",
    cursor: "pointer",
    fontSize: 13,
    color: "#374151",
    fontFamily: PL_FONT,
    userSelect: "none",
    "&:hover": { background: "#F8FAFC" },
    "& input[type='checkbox']": {
      cursor: "pointer",
      width: 14,
      height: 14,
      accentColor: "#4338CA",
      flexShrink: 0,
    },
  },
  colPickerReset: {
    margin: "6px 12px 0",
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "transparent",
    color: "#6B7280",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: PL_FONT,
    textAlign: "center",
    "&:hover": { background: "#F3F4F6", color: "#374151" },
  },

  /* filter icon button (inside header cell) */
  filterIconBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    borderRadius: 3,
    color: PL_TH_TEXT,
    opacity: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.14)" },
  },
  filterIconBtnActive: {
    opacity: 1,
    color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)",
    borderRadius: 3,
  },

  /* filter panel (portal) */
  filterPanel: {
    position: "fixed",
    zIndex: 99,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px",
    minWidth: 180,
    fontFamily: PL_FONT,
  },

  filterTextInput: {
    flex: 1,
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 6px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%",
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 4px",
    outline: "none",
    fontFamily: PL_FONT,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0,
    width: 20,
    height: 24,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: ACCENT,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, borderColor: ACCENT },
  },
  filterOpBtnActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },
  filterWrap: { display: "flex", alignItems: "center", gap: 3, height: 24 },

  /* operator dropdown (portal) */
  opDropdown: {
    position: "fixed",
    zIndex: 101,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    overflow: "hidden",
    fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block",
    width: "100%",
    padding: "8px 14px",
    fontSize: 12,
    color: "#374151",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: PL_FONT,
    "&:hover": { background: ACCENT2, color: ACCENT },
  },
  opDropdownItemActive: { background: ACCENT2, color: ACCENT, fontWeight: 600 },

  /* pagination */
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderTop: "1px solid #E2E8F0",
    background: "#FAFAFA",
    flexShrink: 0,
    gap: 12,
    flexWrap: "wrap",
  },
  paginationInfo: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  paginationRight: { display: "flex", alignItems: "center", gap: 8 },
  paginationControls: { display: "flex", alignItems: "center", gap: 3 },
  pageSizeLabel: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: PL_FONT,
    whiteSpace: "nowrap",
  },
  pageSizeSelect: {
    height: 28,
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    fontSize: 12,
    color: "#374151",
    padding: "0 6px",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    fontFamily: PL_FONT,
    "&:focus": { borderColor: ACCENT },
  },
  pageBtn: {
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    background: "#fff",
    fontSize: 12,
    color: TH_BG_BAND,
    cursor: "pointer",
    fontFamily: PL_FONT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    "&:hover:not(:disabled)": {
      background: "#F1F5F9",
      borderColor: "#94A3B8",
      color: TH_BG,
    },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: TH_BG_BAND,
    borderColor: TH_BG_BAND,
    color: "#fff",
    fontWeight: 600,
    "&:hover": { background: TH_BG_BAND },
  },
  pageDots: {
    fontSize: 12,
    color: "#6B7280",
    padding: "0 4px",
    userSelect: "none",
  },

  errorBox: {
    padding: "48px 24px",
    textAlign: "center",
    fontFamily: PL_FONT,
    color: "#D64550",
    fontSize: 13,
  },
  stateBox: {
    padding: "48px 24px",
    textAlign: "center",
    fontFamily: PL_FONT,
    color: "#6B7280",
    fontSize: 13,
  },
}));

/* ─── Sticky header helpers ─────────────────────────────────────── */
const thCore = (top, h, z, padding, bg) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: PL_TH_TEXT,
  verticalAlign: "middle",
  padding,
  whiteSpace: "nowrap",
  border: "none",
  overflow: "visible",
  boxShadow: `inset -1px 0 0 ${PL_TH_BORDER}, inset 0 -1px 0 ${PL_TH_BORDER}`,
});

const thFixedGroup = (fixedW) => ({
  ...thCore(0, GH, Z_HEAD + 1, "10px 16px", PL_TH_BG),
  left: 0,
  ...dynamicColSize(fixedW),
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  textAlign: "left",
});

const thFixedCol = (align, col) => ({
  ...thCore(GH, CH, Z_HEAD + 1, "10px 14px", PL_TH_BG),
  left: col.left,
  ...dynamicColSize(col.w),
  ...(col.isNum
    ? {}
    : { whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }),
  fontSize: 11,
  fontWeight: 500,
  textAlign: align,
});

const thScrollGroup = () => ({
  ...thCore(0, GH, Z_HEAD, "10px 10px", PL_TH_BG),
  fontSize: 10,
  fontWeight: 700,
  textAlign: "center",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
});

const thScrollCol = (align) => ({
  ...thCore(GH, CH, Z_HEAD, "10px 8px", PL_TH_BG),
  fontSize: 11,
  fontWeight: 500,
  textAlign: align,
});

/* ─── Data row helpers ──────────────────────────────────────────── */
const tdBase = (bg, align, fw = 400, isNumeric = true) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "7px 10px",
  fontSize: 12,
  color: CELL_FG,
  ...cellWhiteSpaceStyle(isNumeric),
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});

const tdFixed = (bg, align, col, fw, isLast = false) => ({
  ...tdBase(bg, align, fw, !!col.isNum),
  position: "sticky",
  left: col.left,
  zIndex: Z_STICKY,
  ...dynamicColSize(col.w),
  color: CELL_FG,
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});

function ColsIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}

function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export default function ThreeYearView({
  filters = {},
  pageTitle = "Waterfall / EAC — 3 Year View",
  downloadRef,
  setParentDownloading,
}) {
  const plClasses = usePlByProgramStyles();
  const localClasses = useStyles();
  const classes = { ...plClasses, ...localClasses };
  useReportFonts();

  /* ── API data ── */
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── Column visibility ── */
  const [hiddenCols, setHiddenCols] = useState(DEFAULT_HIDDEN);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  /* ── Table filters ── */
  const [searchProjectNum, setSearchProjectNum] = useState("");
  const [searchContractName, setSearchContractName] = useState("");
  const [searchCustomerName, setSearchCustomerName] = useState("");
  const [searchBu, setSearchBu] = useState("");
  const [searchPm, setSearchPm] = useState("");
  const [searchPopStart, setSearchPopStart] = useState("");
  const [searchPopEnd, setSearchPopEnd] = useState("");
  const [searchContractType, setSearchContractType] = useState("");
  const [colFilters, setColFilters] = useState({});
  const [openOpKey, setOpenOpKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const opBtnRefs = useRef({});

  /* ── Filter panel ── */
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ── Sorting — array of {key, dir} for multi-level sort ── */
  const [sortKeys, setSortKeys] = useState([]);

  /* ── Close column picker on outside click ── */
  useEffect(() => {
    if (!pickerOpen) return;
    const h = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target))
        setPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [pickerOpen]);

  /* ── Close operator dropdown on outside click ── */
  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  /* ── Close filter panel on outside click ── */
  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]")
      )
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);
  /* ── Fetch table data ── */
  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildWaterfallEacApiFilters),
    [filters],
  );

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const { blob, filename } = await downloadThreeYearViewAPI(
        buildWaterfallEacDownloadFilters(filters),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* silent */
    } finally {
      setParentDownloading?.(false);
    }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  useEffect(() => {
    setLoading(true);
    setError(null);
    getThreeYearViewAPI(buildWaterfallEacApiFilters(filters))
      .then((res) => {
        if (res?.status === 200 && res?.data) setApiData(res.data);
        else {
          setError(res?.message || "Failed to load data");
          setApiData(null);
        }
      })
      .catch(() => {
        setError("Failed to load data");
        setApiData(null);
      })
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Reset to page 1 on any filter change ── */
  useEffect(() => {
    setPage(1);
  }, [
    searchProjectNum,
    searchContractName,
    searchCustomerName,
    searchBu,
    searchPm,
    searchPopStart,
    searchPopEnd,
    searchContractType,
    colFilters,
    filters,
  ]);

  /* ── Derived rows ── */
  const allRows = useMemo(() => {
    if (!apiData) return [];
    return [
      transformGrandTotals(apiData.grandTotals),
      ...(apiData.contracts || []).map(transformContract),
    ];
  }, [apiData]);

  const totalRow = allRows[0] || null;
  const contractRows = allRows.slice(1);

  const fiscalYears = apiData?.fiscalYears || [];

  const actualsThroughDate = useMemo(
    () => formatActualsThroughDate(apiData?.actualThroughMonth),
    [apiData?.actualThroughMonth],
  );

  /* ── Visible Columns (recomputed when cols toggled) ── */
  const visibleFixed = useMemo(() => {
    let left = 0;
    return FIXED_DEFS.filter((c) => !hiddenCols.has(c.key)).map((c) => {
      const col = { ...c, left };
      left += c.w;
      return col;
    });
  }, [hiddenCols]);

  const visibleYearMetrics = useMemo(
    () => YEAR_METRICS.filter((m) => !hiddenCols.has(m.key)),
    [hiddenCols],
  );

  const fixedW = visibleFixed.reduce((s, c) => s + c.w, 0);
  const yearBlockW = visibleYearMetrics.reduce((s, m) => s + m.w, 0);
  const yearSpan = visibleYearMetrics.length;
  const tableW = fixedW + YEAR_GROUPS.length * yearBlockW;

  const hiddenCount = hiddenCols.size;

  const toggleCol = (key) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  /* ── Column filter helpers ── */
  const updateColFilter = (key, field, value) =>
    setColFilters((prev) => ({
      ...prev,
      [key]: { op: "gt", ...prev[key], [field]: value },
    }));
  const clearColFilter = (key) =>
    setColFilters((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  const isNumActive = (key) => {
    const f = colFilters[key];
    return f && f.value !== "" && f.value !== undefined;
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };
  const selectOp = (key, op) => {
    updateColFilter(key, "op", op);
    setOpenOpKey(null);
  };

  /* ── Filter icon helpers ── */
  const getTextFilterEntry = (key) => {
    const map = {
      projectNum: [searchProjectNum, setSearchProjectNum, "Search ID…"],
      contractName: [searchContractName, setSearchContractName, "Search name…"],
      customerName: [
        searchCustomerName,
        setSearchCustomerName,
        "Search customer…",
      ],
      bu: [searchBu, setSearchBu, "Search BU…"],
      pm: [searchPm, setSearchPm, "Search PM…"],
      popStart: [searchPopStart, setSearchPopStart, "Start date…"],
      popEnd: [searchPopEnd, setSearchPopEnd, "End date…"],
      contractType: [searchContractType, setSearchContractType, "Type…"],
    };
    return map[key] || null;
  };

  const isYearMetricKey = (key) => /^year[012]_/.test(key);

  const hasFilterValue = (key) => {
    if (isYearMetricKey(key)) return isNumActive(key);
    const tf = getTextFilterEntry(key);
    return tf ? !!tf[0] : isNumActive(key);
  };

  const renderFilterContent = (key) => {
    if (isYearMetricKey(key)) return renderNumFilter(key);
    const tf = getTextFilterEntry(key);
    if (tf) return renderTextFilter(tf[0], tf[1], tf[2]);
    return renderNumFilter(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  /* ── Row filter predicate ── */
  const rowPasses = (row) => {
    if (
      searchProjectNum &&
      !String(row.projectNum || "")
        .toLowerCase()
        .includes(searchProjectNum.toLowerCase())
    )
      return false;
    if (
      searchContractName &&
      !String(row.contractName || "")
        .toLowerCase()
        .includes(searchContractName.toLowerCase())
    )
      return false;
    if (
      searchCustomerName &&
      !String(row.customerName || "")
        .toLowerCase()
        .includes(searchCustomerName.toLowerCase())
    )
      return false;
    if (
      !hiddenCols.has("bu") &&
      searchBu &&
      !String(row.bu || "")
        .toLowerCase()
        .includes(searchBu.toLowerCase())
    )
      return false;
    if (
      !hiddenCols.has("pm") &&
      searchPm &&
      !String(row.pm || "")
        .toLowerCase()
        .includes(searchPm.toLowerCase())
    )
      return false;
    if (
      !hiddenCols.has("contractType") &&
      searchContractType &&
      !String(row.contractType || "")
        .toLowerCase()
        .includes(searchContractType.toLowerCase())
    )
      return false;
    if (
      !hiddenCols.has("popStart") &&
      searchPopStart &&
      !String(row.popStart || "")
        .toLowerCase()
        .includes(searchPopStart.toLowerCase())
    )
      return false;
    if (
      !hiddenCols.has("popEnd") &&
      searchPopEnd &&
      !String(row.popEnd || "")
        .toLowerCase()
        .includes(searchPopEnd.toLowerCase())
    )
      return false;
    if (
      isNumActive("tcvCeiling") &&
      !applyNumFilter(row.tcvCeiling, colFilters.tcvCeiling)
    )
      return false;
    if (
      isNumActive("tcvFunded") &&
      !applyNumFilter(row.tcvFunded, colFilters.tcvFunded)
    )
      return false;
    for (const yg of YEAR_GROUPS) {
      for (const m of YEAR_METRICS) {
        const fk = `${yg.key}_${m.key}`;
        if (
          isNumActive(fk) &&
          !applyNumFilter((row[yg.key] || {})[m.key], colFilters[fk])
        )
          return false;
      }
    }
    return true;
  };

  /* ── Sorting ── */
  const handleSort = (colKey) =>
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  const onThClick = createSortThClick(handleSort);

  const getSortValue = (row, colKey) => {
    if (FIXED_DEFS.some((c) => c.key === colKey)) return row[colKey];
    const us = colKey.indexOf("_");
    if (us > 0) {
      const ygKey = colKey.slice(0, us);
      const mKey = colKey.slice(us + 1);
      if (YEAR_GROUPS.some((yg) => yg.key === ygKey))
        return (row[ygKey] || {})[mKey];
    }
    return row[colKey];
  };

  const getSortColumnLabel = (colKey) => {
    const fixed = FIXED_DEFS.find((c) => c.key === colKey);
    if (fixed) return fixed.label;
    const us = colKey.indexOf("_");
    if (us > 0) {
      const ygKey = colKey.slice(0, us);
      const mKey = colKey.slice(us + 1);
      const yg = YEAR_GROUPS.find((y) => y.key === ygKey);
      const m = YEAR_METRICS.find((meas) => meas.key === mKey);
      if (yg && m) {
        const fy = fiscalYears[yg.offset];
        const yrLabel = fy ? `FY-${fy}` : `Year +${yg.offset}`;
        return `${yrLabel} ${m.label}`;
      }
    }
    return colKey;
  };

  /* ── Pagination ── */
  const filteredRows = contractRows.filter(rowPasses);
  const visibleRows =
    sortKeys.length === 0
      ? filteredRows
      : [...filteredRows].sort((a, b) =>
          compareBySortKeys(a, b, sortKeys, getSortValue),
        );
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = visibleRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const rangeStart =
    visibleRows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, visibleRows.length);

  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  const hasRowFilters = useMemo(
    () =>
      hasAnyTableFilters(
        searchProjectNum,
        searchContractName,
        searchCustomerName,
        !hiddenCols.has("bu") && searchBu,
        !hiddenCols.has("pm") && searchPm,
        !hiddenCols.has("contractType") && searchContractType,
        !hiddenCols.has("popStart") && searchPopStart,
        !hiddenCols.has("popEnd") && searchPopEnd,
        isNumActive("tcvCeiling"),
        isNumActive("tcvFunded"),
        YEAR_GROUPS.some((yg) =>
          YEAR_METRICS.some((m) => isNumActive(`${yg.key}_${m.key}`)),
        ),
      ),
    [
      searchProjectNum,
      searchContractName,
      searchCustomerName,
      searchBu,
      searchPm,
      searchContractType,
      searchPopStart,
      searchPopEnd,
      hiddenCols,
      colFilters,
    ],
  );

  const displayTotalRow = useMemo(() => {
    if (!totalRow) return null;
    if (!hasRowFilters) return totalRow;
    return (
      aggregateThreeYearRows(filteredRows, YEAR_GROUPS, YEAR_METRICS) ??
      totalRow
    );
  }, [totalRow, hasRowFilters, filteredRows]);

  const contractCount = allRows.length > 0 ? allRows.length - 1 : 0;

  /* ── Filter renderers ── */
  const renderTextFilter = (value, onChange, placeholder) => (
    <div className={classes.filterWrap}>
      <input
        type="text"
        className={classes.filterTextInput}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button className={classes.filterClearBtn} onClick={() => onChange("")}>
          ✕
        </button>
      )}
    </div>
  );

  const renderNumFilter = (colKey) => {
    const f = colFilters[colKey] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getSortColumnLabel(colKey)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateColFilter(colKey, "value", v)}
        onClear={() => clearColFilter(colKey)}
        onToggleOp={() => toggleOpDropdown(colKey)}
        opBtnRef={(el) => {
          opBtnRefs.current[colKey] = el;
        }}
      />
    );
  };

  return (
    <div className={plClasses.root}>
      <div className={plClasses.container}>
        <PlPageHeader
          classes={classes}
          eyebrow="Waterfall / EAC"
          title={pageTitle}
          meta={
            loading
              ? "Loading…"
              : `${contractCount} Contracts${actualsThroughDate ? "" : " · All Forecast"}`
          }
          metaTop={filters.year ? `FY-${filters.year}` : ""}
          date={actualsThroughDate ?? undefined}
        />

        <div className={classes.tableSection}>
          {/* ── Section head ── */}
          <div className={classes.tableSectionHead}>
            <div className={classes.sectionHeadRow}>
              <div>
                <h2 className={classes.tableSectionTitle}>
                  3-Year Financial Projection
                </h2>
                <div className={classes.tableSectionMeta}>
                  {`Live EAC · ${visibleRows.length} of ${contractCount} Contracts`}
                  {fiscalYears.length > 0 &&
                    ` · ${fiscalYears.map((y) => `FY-${y}`).join(" · ")}`}
                  {sortKeys.length > 0 && (
                    <span
                      style={{ marginLeft: 8, color: ACCENT, fontWeight: 500 }}
                    >
                      · sorted by{" "}
                      {sortKeys
                        .map((s) => getSortColumnLabel(s.key))
                        .join(", ")}
                    </span>
                  )}
                </div>
              </div>

              <div className={classes.sectionHeadRight}>
                <div ref={pickerRef} className={classes.colPickerWrap}>
                  <button
                    type="button"
                    className={classes.colPickerBtn}
                    onClick={() => setPickerOpen((p) => !p)}
                  >
                    <ColsIcon />
                    Columns
                    {hiddenCount > 0 && (
                      <span className={classes.colHiddenBadge}>
                        {hiddenCount} Hidden
                      </span>
                    )}
                  </button>
                  {pickerOpen && (
                    <div className={classes.colPickerDropdown}>
                      {/* Fixed columns */}
                      <div className={classes.colPickerHeader}>
                        Fixed Columns
                      </div>
                      {FIXED_DEFS.map((c) => (
                        <label key={c.key} className={classes.colPickerItem}>
                          <input
                            type="checkbox"
                            checked={!hiddenCols.has(c.key)}
                            onChange={() => toggleCol(c.key)}
                          />
                          {c.label}
                        </label>
                      ))}

                      {/* Year metrics — one checkbox hides/shows that metric across all 3 year groups */}
                      <div
                        className={classes.colPickerHeader}
                        style={{ marginTop: 4 }}
                      >
                        Year Metrics
                      </div>
                      {YEAR_METRICS.map((m) => (
                        <label key={m.key} className={classes.colPickerItem}>
                          <input
                            type="checkbox"
                            checked={!hiddenCols.has(m.key)}
                            onChange={() => toggleCol(m.key)}
                          />
                          {m.label}
                        </label>
                      ))}

                      {hiddenCount > 0 && (
                        <button
                          type="button"
                          className={classes.colPickerReset}
                          onClick={() => setHiddenCols(new Set())}
                        >
                          Show All Columns
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading / error / empty ── */}
          {loading && <Loading />}
          {!loading && error && <div className={classes.errorBox}>{error}</div>}
          {!loading && !error && allRows.length === 0 && (
            <div className={classes.stateBox}>
              No data for the selected filters.
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && allRows.length > 0 && (
            <div className={classes.tableBody}>
              <div
                className={classes.tableScrollPane}
                onScroll={handleTableScroll}
              >
                <table
                  className={classes.table}
                  style={{ width: tableW, minWidth: tableW }}
                >
                  <colgroup>
                    {visibleFixed.map((c) => (
                      <col key={c.key} style={dynamicColSize(c.w)} />
                    ))}
                    {YEAR_GROUPS.map((yg) =>
                      visibleYearMetrics.map((m) => (
                        <col
                          key={`${yg.key}_${m.key}`}
                          style={dynamicColSize(m.w)}
                        />
                      )),
                    )}
                  </colgroup>

                  <thead>
                    {/* ── Group header row ── */}
                    <tr style={{ height: GH }}>
                      <th
                        colSpan={visibleFixed.length}
                        style={thFixedGroup(fixedW)}
                        aria-hidden
                      />
                      {YEAR_GROUPS.map((yg) => {
                        const fy = fiscalYears[yg.offset];
                        return yearSpan > 0 ? (
                          <th
                            key={yg.key}
                            colSpan={yearSpan}
                            style={thScrollGroup()}
                          >
                            {fy
                              ? `FY-${fy} · Year +${yg.offset}`
                              : `Year +${yg.offset}`}
                          </th>
                        ) : null;
                      })}
                    </tr>

                    {/* ── Column header row ── */}
                    <tr>
                      {visibleFixed.map((c) => {
                        const fActive =
                          openFilterKey === c.key || hasFilterValue(c.key);
                        return (
                          <th
                            key={c.key}
                            style={{
                              ...thFixedCol(c.align, c),
                              cursor: "pointer",
                            }}
                            onClick={onThClick(c.key)}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  c.align === "right"
                                    ? "flex-end"
                                    : "space-between",
                                gap: 4,
                                minWidth: 0,
                              }}
                            >
                              <WaterfallThLabel
                                label={c.label}
                                colKey={c.key}
                                sortKeys={sortKeys}
                                getLabel={getSortColumnLabel}
                                highlight={openFilterKey === c.key}
                                align={c.align}
                              />
                              <button
                                ref={(el) => {
                                  filterBtnRefs.current[c.key] = el;
                                }}
                                data-filter-btn
                                className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                onClick={() => toggleFilter(c.key)}
                                title="Filter"
                              >
                                <FilterFunnelIcon />
                              </button>
                            </div>
                          </th>
                        );
                      })}
                      {YEAR_GROUPS.map((yg) =>
                        visibleYearMetrics.map((m) => {
                          const colKey = `${yg.key}_${m.key}`;
                          const fActive =
                            openFilterKey === colKey || hasFilterValue(colKey);
                          return (
                            <th
                              key={colKey}
                              style={{
                                ...thScrollCol("right"),
                                ...dynamicColSize(m.w),
                                cursor: "pointer",
                              }}
                              onClick={onThClick(colKey)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: 3,
                                  minWidth: 0,
                                }}
                              >
                                <WaterfallThLabel
                                  label={m.label}
                                  colKey={colKey}
                                  sortKeys={sortKeys}
                                  getLabel={getSortColumnLabel}
                                  highlight={openFilterKey === colKey}
                                  align="right"
                                />
                                <button
                                  ref={(el) => {
                                    filterBtnRefs.current[colKey] = el;
                                  }}
                                  data-filter-btn
                                  className={`${classes.filterIconBtn} ${fActive ? classes.filterIconBtnActive : ""}`}
                                  onClick={() => toggleFilter(colKey)}
                                  title="Filter"
                                >
                                  <FilterFunnelIcon />
                                </button>
                              </div>
                            </th>
                          );
                        }),
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {/* Grand total row — always visible, not paginated */}
                    {displayTotalRow &&
                      (() => {
                        const bg = TOTAL_BG;
                        const fw = 700;
                        return (
                          <tr key="total">
                            {visibleFixed.map((c, ci) => {
                              let display = displayTotalRow[c.key] ?? "";
                              if (
                                c.key === "tcvCeiling" ||
                                c.key === "tcvFunded"
                              )
                                display =
                                  displayTotalRow[c.key] != null
                                    ? fmtMoney2(displayTotalRow[c.key])
                                    : "";
                              return (
                                <td
                                  key={c.key}
                                  style={{
                                    ...tdFixed(
                                      bg,
                                      c.align,
                                      c,
                                      fw,
                                      ci === visibleFixed.length - 1,
                                    ),
                                    color: TOTAL_FG,
                                  }}
                                  title={
                                    typeof display === "string" ? display : ""
                                  }
                                >
                                  {display}
                                </td>
                              );
                            })}
                            {YEAR_GROUPS.map((yg, gi) =>
                              visibleYearMetrics.map((m) => {
                                const v = (displayTotalRow[yg.key] || {})[
                                  m.key
                                ];
                                const stripe = gi % 2 === 1 ? "#E8F5EC" : bg;
                                return (
                                  <td
                                    key={`${yg.key}_${m.key}`}
                                    style={{
                                      ...tdBase(stripe, "right", 700),
                                      color: v < 0 ? NEG : TOTAL_FG,
                                      ...dynamicColSize(m.w),
                                    }}
                                  >
                                    {fmtCell(v, m.isPct)}
                                  </td>
                                );
                              }),
                            )}
                          </tr>
                        );
                      })()}

                    {/* Paged contract rows */}
                    {pagedRows.map((row, ri) => {
                      const globalIdx = (safePage - 1) * pageSize + ri;
                      const bg = globalIdx % 2 === 0 ? EVEN_BG : ODD_BG;
                      return (
                        <tr key={row.projectNum || `r-${ri}`}>
                          {visibleFixed.map((c, ci) => {
                            let display = row[c.key] ?? "";
                            if (c.key === "tcvCeiling" || c.key === "tcvFunded")
                              display =
                                row[c.key] != null ? fmtMoney2(row[c.key]) : "";
                            return (
                              <td
                                key={c.key}
                                style={{
                                  ...tdFixed(
                                    bg,
                                    c.align,
                                    c,
                                    400,
                                    ci === visibleFixed.length - 1,
                                  ),
                                  color: CELL_FG,
                                }}
                                title={
                                  typeof display === "string" ? display : ""
                                }
                              >
                                {display}
                              </td>
                            );
                          })}
                          {YEAR_GROUPS.map((yg, gi) =>
                            visibleYearMetrics.map((m) => {
                              const v = (row[yg.key] || {})[m.key];
                              const neg = typeof v === "number" && v < 0;
                              const stripe =
                                gi % 2 === 1
                                  ? bg === EVEN_BG
                                    ? "#FAFAFA"
                                    : "#E8EEF5"
                                  : bg;
                              return (
                                <td
                                  key={`${yg.key}_${m.key}`}
                                  style={{
                                    ...tdBase(stripe, "right", 400),
                                    color: neg ? NEG : CELL_FG,
                                    ...dynamicColSize(m.w),
                                  }}
                                >
                                  {fmtCell(v, m.isPct)}
                                </td>
                              );
                            }),
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination bar ── */}
              <div className={classes.paginationBar}>
                <span className={classes.paginationInfo}>
                  {visibleRows.length === 0
                    ? "No contracts match filters"
                    : `${rangeStart}–${rangeEnd} of ${visibleRows.length} Contracts`}
                </span>
                <div className={classes.paginationRight}>
                  <span className={classes.pageSizeLabel}>Rows Per Page</span>
                  <select
                    className={classes.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <div className={classes.paginationControls}>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === 1}
                      onClick={() => setPage(1)}
                      title="First"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="7,2 3,6 7,10" />
                        <polyline points="11,2 7,6 11,10" />
                      </svg>
                    </button>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === 1}
                      onClick={() => setPage((p) => p - 1)}
                      title="Previous"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="8,2 4,6 8,10" />
                      </svg>
                    </button>
                    {pageNumbers.map((p, i) => (
                      <React.Fragment key={p}>
                        {i > 0 && pageNumbers[i - 1] < p - 1 && (
                          <span className={classes.pageDots}>…</span>
                        )}
                        <button
                          className={`${classes.pageBtn} ${p === safePage ? classes.pageBtnActive : ""}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      title="Next"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="4,2 8,6 4,10" />
                      </svg>
                    </button>
                    <button
                      className={classes.pageBtn}
                      disabled={safePage === totalPages}
                      onClick={() => setPage(totalPages)}
                      title="Last"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="5,2 9,6 5,10" />
                        <polyline points="1,2 5,6 1,10" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            className={classes.filterPanel}
            style={{ top: filterPanelPos.top, left: filterPanelPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={colFilters[openOpKey]?.op || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}
